import { Box, Stack, Text } from '@mantine/core';
import { IconPhoto } from '@tabler/icons-react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import type { UploadQueueRow } from '@/db';
import { UploadItem } from './UploadItem';

import type { JSX } from "react";

interface UploadQueueListProps {
  items: UploadQueueRow[];
  /** Forwarded to each row's edit affordances (e.g. close the modal on navigate). */
  onNavigate?: (() => void) | undefined;
}

export function UploadQueueList({ items, onNavigate }: UploadQueueListProps): JSX.Element {
  const [listRef] = useAutoAnimate<HTMLDivElement>({ duration: 220, easing: 'ease-in-out' });

  if (items.length === 0) {
    return (
      <Stack
        align="center"
        justify="center"
        gap={8}
        style={{ flex: 1, padding: 32, color: '#807563', minHeight: 240 }}
      >
        <Box
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#f6f1ea',
            color: '#aea38f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconPhoto size={26} stroke={1.5} />
        </Box>
        <Text size="sm" fw={500} c="#4a4137">
          No files in queue
        </Text>
        <Text size="xs" c="#807563" ta="center" maw={320} style={{ lineHeight: 1.5 }}>
          Drop images into the panel on the left, or click <em>Browse files…</em> to pick from your
          computer.
        </Text>
      </Stack>
    );
  }

  return (
    <Box
      ref={listRef}
      role="list"
      aria-label="Upload queue"
      // Cap = viewport minus the interface above the list (modal chrome + uploader
      // card + queue header ≈ 440px) + a nice margin; floor keeps a few rows visible.
      style={{ flex: 1, overflowY: 'auto', padding: 8, maxHeight: 'calc(100vh - 480px)', minHeight: 240 }}
    >
      {items.map((item, i) => (
        <UploadItem
          key={item.id}
          item={item}
          isLast={i === items.length - 1}
          onNavigate={onNavigate}
        />
      ))}
    </Box>
  );
}
